import React, { useState } from 'react';
import { XIcon, StarIcon, InfoIcon, SaveIcon, CheckCircleIcon, AlertCircleIcon, ArrowUpIcon, ArrowDownIcon, CalendarIcon, UserIcon, BarChartIcon } from 'lucide-react';
export function QuoteScoreModal({
  quote,
  onClose,
  onUpdate
}) {
  const [scoreDetails, setScoreDetails] = useState(quote.scoreDetails || {
    quoteValue: 6,
    customerType: 7,
    recentActivity: 5,
    expirationDate: 7,
    winProbability: 5,
    approvalStage: 5,
    availableStock: 5
  });
  const handleSliderChange = (criteria, value) => {
    setScoreDetails({
      ...scoreDetails,
      [criteria]: parseInt(value)
    });
  };
  const handleSubmit = () => {
    const newScore = calculateTotalScore();
    onUpdate(quote.id, {
      ...scoreDetails,
      totalScore: newScore
    });
    onClose();
  };
  const calculateTotalScore = () => {
    // Weights for each criteria
    const weights = {
      quoteValue: 3,
      customerType: 2,
      recentActivity: 2,
      expirationDate: 2,
      winProbability: 2,
      approvalStage: 2,
      availableStock: 1.5
    };
    // Calculate weighted score
    let weightedTotal = 0;
    let weightSum = 0;
    for (const [key, value] of Object.entries(scoreDetails)) {
      weightedTotal += value * weights[key];
      weightSum += weights[key];
    }
    // Convert to 0-100 scale
    return Math.round(weightedTotal / weightSum * 10);
  };
  const getScoreColor = score => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-blue-500';
    if (score >= 40) return 'text-amber-500';
    return 'text-red-500';
  };
  const getScoreBgColor = score => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-blue-100';
    if (score >= 40) return 'bg-amber-100';
    return 'bg-red-100';
  };
  const getScoreLabel = score => {
    if (score >= 80) return 'Alta prioridad';
    if (score >= 60) return 'Prioridad media';
    if (score >= 40) return 'Prioridad baja';
    return 'Necesita atención';
  };
  const totalScore = calculateTotalScore();
  const criteriaLabels = {
    quoteValue: 'Valor de cotización',
    customerType: 'Tipo de cliente',
    recentActivity: 'Actividad reciente',
    expirationDate: 'Fecha de expiración',
    winProbability: 'Probabilidad de venta',
    approvalStage: 'Etapa de aprobación',
    availableStock: 'Stock disponible'
  };
  const criteriaDescriptions = {
    quoteValue: 'Valor monetario de la cotización',
    customerType: 'Importancia y categoría del cliente',
    recentActivity: 'Interacción reciente del cliente',
    expirationDate: 'Tiempo hasta vencimiento',
    winProbability: 'Probabilidad estimada de venta',
    approvalStage: 'Etapa en el proceso de aprobación',
    availableStock: 'Disponibilidad de productos'
  };
  return <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm" data-id="element-4478">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-4479">
        <div className="border-b border-gray-200 p-4 flex justify-between items-center" data-id="element-4480">
          <div className="flex items-center" data-id="element-4481">
            <StarIcon size={20} className="mr-2 text-blue-600" data-id="element-4482" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-4483">
              Puntuación de Cotización
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full p-1.5 transition-colors" data-id="element-4484">
            <XIcon size={16} data-id="element-4485" />
          </button>
        </div>
        <div className="p-6" data-id="element-4486">
          {/* Score overview */}
          <div className="flex items-center justify-between mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-4487">
            <div className="flex items-center" data-id="element-4488">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${getScoreBgColor(totalScore)}`} data-id="element-4489">
                <span className={`text-2xl font-bold ${getScoreColor(totalScore)}`} data-id="element-4490">
                  {totalScore}
                </span>
              </div>
              <div className="ml-4" data-id="element-4491">
                <div className="text-sm font-medium text-gray-500" data-id="element-4492">
                  Puntuación total
                </div>
                <div className={`text-lg font-medium ${getScoreColor(totalScore)}`} data-id="element-4493">
                  {getScoreLabel(totalScore)}
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-500 bg-white px-3 py-1.5 rounded-md border border-gray-200" data-id="element-4494">
              ID: {quote.id}
            </div>
          </div>
          {/* Criteria sliders */}
          <div className="space-y-5" data-id="element-4495">
            <div className="flex items-center justify-between" data-id="element-4496">
              <h3 className="text-base font-medium text-gray-700" data-id="element-4497">
                Criterios de evaluación
              </h3>
              <div className="text-xs text-gray-500 flex items-center" data-id="element-4498">
                <InfoIcon size={12} className="mr-1" data-id="element-4499" />
                Desliza para ajustar
              </div>
            </div>
            {Object.entries(criteriaLabels).map(([key, label]) => <div key={key} className="space-y-2" data-id="element-4500">
                <div className="flex justify-between items-center" data-id="element-4501">
                  <div data-id="element-4502">
                    <label className="text-sm font-medium text-gray-700" data-id="element-4503">
                      {label}
                    </label>
                    <p className="text-xs text-gray-500" data-id="element-4504">
                      {criteriaDescriptions[key]}
                    </p>
                  </div>
                  <span className={`text-sm font-medium ${getScoreColor(scoreDetails[key] * 10)}`} data-id="element-4505">
                    {scoreDetails[key]}/10
                  </span>
                </div>
                <input type="range" min="1" max="10" value={scoreDetails[key]} onChange={e => handleSliderChange(key, e.target.value)} className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer" data-id="element-4506" />
                <div className="flex justify-between text-xs text-gray-400" data-id="element-4507">
                  <span data-id="element-4508">Bajo</span>
                  <span data-id="element-4509">Medio</span>
                  <span data-id="element-4510">Alto</span>
                </div>
              </div>)}
          </div>
        </div>
        <div className="border-t border-gray-200 p-4 flex justify-end" data-id="element-4511">
          <button onClick={onClose} className="mr-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm" data-id="element-4512">
            Cancelar
          </button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center" data-id="element-4513">
            <SaveIcon size={16} className="mr-2" data-id="element-4514" />
            Guardar cambios
          </button>
        </div>
      </div>
    </div>;
}